"use client";

import React, {useEffect, useState} from 'react';
import ContentSettingsDialog from './ContentSettingsDialog.tsx';
import LocaleFieldWrapper from '../../components/LocaleFieldWrapper.tsx';
import {useLocale} from '../../context/localeContext.tsx';
import {BlockBase, MediaElement} from '../../interfaces.ts';
import {resolveSrc} from '../../utils.ts';

interface MediaBlockSettingsDialogProps {
  block: BlockBase<any, any>;
  contentData: any; // This will contain the actual content data for this block
  onClose: () => void;
  onSave: (updatedContentData: any) => void;
  availableLocales: string[];
  fallbackLocale?: string;
  newMediaCallback?: (args: { blockId: string }) => Promise<string>;
  mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>;
  files?: MediaElement[];
}

const MediaBlockSettingsDialog: React.FC<MediaBlockSettingsDialogProps> = ({
  block,
  contentData,
  onClose,
  onSave,
  availableLocales,
  newMediaCallback,
  mediaLibraryCallback,
  files,
}) => {
  const { activeLocale } = useLocale();

  const [localContent, setLocalContent] = useState<{
    [key: string]: { src: string; altText: string; type?: 'image' | 'video' | 'gallery'; gallerySrc?: string[] };
  }>(
    availableLocales.reduce((acc: any, locale: string) => {
      // Use default content from template if no specific content exists for this locale
      const defaultContent = block.defaultContent?.[locale];
      const currentContent = contentData?.[locale]?.[block.id];

      acc[locale] = {
        src: currentContent?.src || defaultContent?.src || '',
        altText: currentContent?.altText || defaultContent?.altText || '',
        type: currentContent?.type || defaultContent?.type || 'image',
        gallerySrc: currentContent?.gallerySrc || defaultContent?.gallerySrc || [],
      };
      return acc;
    }, {})
  );

  useEffect(() => {
    const currentContent = localContent[activeLocale];
    if (!currentContent) {
      setLocalContent((prev) => ({
        ...prev,
        [activeLocale]: { src: '', altText: '', type: 'image', gallerySrc: [] },
      }));
    }
  }, [activeLocale, localContent]);

  const handleSave = () => {
    onSave({
      ...contentData,
      [activeLocale]: {
        ...contentData[activeLocale],
        [block.id]: localContent[activeLocale],
      }
    });
  };

  const handleSrcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalContent((prev: any) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], src: e.target.value },
    }));
  };

  const handleAltTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalContent((prev: any) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], altText: e.target.value },
    }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalContent((prev: any) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], type: e.target.value as 'image' | 'video' | 'gallery' },
    }));
  };

  const handleNewMediaClick = async () => {
    if (newMediaCallback) {
      try {
        const url = await newMediaCallback({ blockId: block.id });
        setLocalContent((prev: any) => ({
          ...prev,
          [activeLocale]: { ...prev[activeLocale], src: url },
        }));
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  const handleMediaLibrary = async () => {
    if (mediaLibraryCallback) {
      try {
        const url = await mediaLibraryCallback({ blockId: block.id });
        const currentType = localContent[activeLocale]?.type || 'image';
        
        if (currentType === 'gallery') {
          setLocalContent((prev: any) => ({
            ...prev,
            [activeLocale]: { 
              ...prev[activeLocale], 
              gallerySrc: [...(prev[activeLocale]?.gallerySrc || []), url] 
            },
          }));
        } else {
          setLocalContent((prev: any) => ({
            ...prev,
            [activeLocale]: { ...prev[activeLocale], src: url },
          }));
        }
      } catch (error) {
        console.error('Media library selection failed:', error);
      }
    }
  };

  const handleRemoveGalleryImage = (index: number) => {
    setLocalContent((prev: any) => ({
      ...prev,
      [activeLocale]: {
        ...prev[activeLocale],
        gallerySrc: (prev[activeLocale]?.gallerySrc || []).filter((_: any, i: number) => i !== index),
      },
    }));
  };

  const handleMoveGalleryImageLeft = (index: number) => {
    if (index === 0) return;
    setLocalContent((prev: any) => {
      const gallerySrc = [...(prev[activeLocale]?.gallerySrc || [])];
      [gallerySrc[index - 1], gallerySrc[index]] = [gallerySrc[index], gallerySrc[index - 1]];
      return {
        ...prev,
        [activeLocale]: {
          ...prev[activeLocale],
          gallerySrc,
        },
      };
    });
  };

  const handleMoveGalleryImageRight = (index: number) => {
    const gallerySrcLength = localContent[activeLocale]?.gallerySrc?.length || 0;
    if (index === gallerySrcLength - 1) return;
    setLocalContent((prev: any) => {
      const gallerySrc = [...(prev[activeLocale]?.gallerySrc || [])];
      [gallerySrc[index], gallerySrc[index + 1]] = [gallerySrc[index + 1], gallerySrc[index]];
      return {
        ...prev,
        [activeLocale]: {
          ...prev[activeLocale],
          gallerySrc,
        },
      };
    });
  };

  const handleAddGalleryImage = async () => {
    if (newMediaCallback) {
      try {
        const url = await newMediaCallback({ blockId: block.id });
        setLocalContent((prev: any) => ({
          ...prev,
          [activeLocale]: { 
            ...prev[activeLocale], 
            gallerySrc: [...(prev[activeLocale]?.gallerySrc || []), url] 
          },
        }));
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }
  };

  return (
    <ContentSettingsDialog
      title="MediaBlock Settings"
      onClose={onClose}
      onSave={handleSave}
    >
      <LocaleFieldWrapper>
        <label className="block mb-2">
          Type ({activeLocale.toUpperCase()}):
          <select
            className="w-full border border-gray-300 rounded mt-1 p-1"
            value={localContent?.[activeLocale]?.type || 'image'}
            onChange={handleTypeChange}
          >
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="gallery">Gallery</option>
          </select>
        </label>
      </LocaleFieldWrapper>

      {(localContent?.[activeLocale]?.type || 'image') !== 'gallery' && (
        <LocaleFieldWrapper>
          <div className="flex items-end space-x-4">
            <label className="block flex-1">
              {(localContent?.[activeLocale]?.type || 'image') === 'video' ? 'YouTube URL' : 'Source'} ({activeLocale.toUpperCase()}):
              <input
                type="text"
                className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
                value={localContent?.[activeLocale]?.src || ''}
                onChange={handleSrcChange}
                placeholder={(localContent?.[activeLocale]?.type || 'image') === 'video' ? 'https://www.youtube.com/watch?v=...' : ''}
              />
            </label>
            {newMediaCallback && ((localContent?.[activeLocale]?.type || 'image') === 'image') && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={handleNewMediaClick}
              >
                New Media
              </button>
            )}
            {mediaLibraryCallback && ((localContent?.[activeLocale]?.type || 'image') === 'image') && (
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                onClick={handleMediaLibrary}
              >
                Select from Media Library
              </button>
            )}
          </div>
        </LocaleFieldWrapper>
      )}

      {(localContent?.[activeLocale]?.type || 'image') === 'gallery' && (
        <LocaleFieldWrapper>
          <div className="block mb-4">
            <label className="block mb-2 font-semibold">
              Gallery Images ({activeLocale.toUpperCase()}):
            </label>
            <div className="flex gap-2 mb-3">
              {newMediaCallback && (
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  onClick={handleAddGalleryImage}
                >
                  Upload Image
                </button>
              )}
              {mediaLibraryCallback && (
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  onClick={handleMediaLibrary}
                >
                  Select from Media Library
                </button>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto p-2 bg-gray-50 rounded border border-gray-200">
              {(localContent[activeLocale]?.gallerySrc || []).length === 0 ? (
                <div className="text-gray-400 text-sm py-4">No images added yet</div>
              ) : (
                (localContent[activeLocale]?.gallerySrc || []).map((imgSrc: string, index: number) => {
                  const resolvedImgSrc = resolveSrc(imgSrc, files) || imgSrc;
                  return (
                    <div
                      key={index}
                      className="relative flex-shrink-0 w-[120px] rounded border border-gray-300"
                    >
                      <div className="w-[120px] h-[120px] overflow-hidden">
                        <img
                          src={resolvedImgSrc}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveGalleryImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full border-0 bg-red-600/80 text-white cursor-pointer text-sm flex items-center justify-center p-0 leading-none hover:bg-red-700/90"
                        title="Remove image"
                      >
                        ×
                      </button>
                      <div className="flex gap-1 p-1 bg-white">
                        <button
                          onClick={() => handleMoveGalleryImageLeft(index)}
                          disabled={index === 0}
                          className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                          title="Move left"
                        >
                          ← Left
                        </button>
                        <button
                          onClick={() => handleMoveGalleryImageRight(index)}
                          disabled={index === (localContent[activeLocale]?.gallerySrc || []).length - 1}
                          className="flex-1 px-2 py-1 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                          title="Move right"
                        >
                          Right →
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </LocaleFieldWrapper>
      )}
      
      <LocaleFieldWrapper>
        <label className="block mb-2 mt-2">
          Alt Text ({activeLocale.toUpperCase()}):
          <input
            type="text"
            className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
            value={localContent?.[activeLocale]?.altText || ''}
            onChange={handleAltTextChange}
          />
        </label>
      </LocaleFieldWrapper>
    </ContentSettingsDialog>
  );
};

export default MediaBlockSettingsDialog;